"use client";

import { useQuery } from "@tanstack/react-query";

import { ThemedSelect, themedSelectTriggerClass } from "@/components/ui-system/select";
import { listTenants } from "@/lib/api/endpoints/tenants";
import { useAuthStore } from "@/modules/auth/store";

type WorkspaceScopeFilterProps = {
  ariaLabel?: string;
  className?: string;
  label?: string;
  showLabel?: boolean;
  includeAllOption?: boolean;
  allOptionLabel?: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  triggerClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
};

const defaultTriggerClassName = themedSelectTriggerClass;
const defaultMenuClassName =
  "rounded-2xl border-card-border bg-card p-1.5 shadow-[0_12px_30px_-20px_rgba(15,23,42,0.16)]";
const defaultOptionClassName = "rounded-xl text-[13px]";

export function WorkspaceScopeFilter({
  ariaLabel = "Workspace filter",
  className,
  label = "Workspace",
  showLabel = false,
  includeAllOption = false,
  allOptionLabel = "All Workspaces",
  value,
  onChange,
  triggerClassName = defaultTriggerClassName,
  menuClassName = defaultMenuClassName,
  optionClassName = defaultOptionClassName,
}: WorkspaceScopeFilterProps) {
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const setTenantId = useAuthStore((s) => s.setTenantId);

  const visible = user?.role === "super_admin" || user?.accountType === "platform";
  const tenantsQuery = useQuery({
    queryKey: ["tenant-memberships", user?.id ?? "", "workspace-scope-filter"],
    queryFn: () => listTenants(),
    enabled: visible && Boolean(user?.id),
    staleTime: 60_000,
  });

  if (!visible) {
    return null;
  }

  const options = (tenantsQuery.data ?? user?.tenants ?? []).map((tenant) => ({
    value: tenant.id,
    label: tenant.name,
    description: tenant.domain ?? tenant.id,
  }));
  const selectOptions = includeAllOption ? [{ value: "all", label: allOptionLabel }, ...options] : options;
  const isControlled = value !== undefined;
  const controlledValue = isControlled ? value : tenantId;
  const selectValue = includeAllOption && !controlledValue ? "all" : (controlledValue ?? options[0]?.value ?? "");

  return (
    <div className={className}>
      {showLabel ? (
        <span className="mb-1 block text-micro font-semibold uppercase tracking-[0.12em] text-muted">
          {label}
        </span>
      ) : null}
      <ThemedSelect
        value={selectValue}
        onChange={(nextValue) => {
          const resolvedValue = includeAllOption && nextValue === "all" ? null : (nextValue || null);
          if (onChange) {
            onChange(resolvedValue);
            return;
          }
          if (resolvedValue !== tenantId) {
            setTenantId(resolvedValue);
          }
        }}
        options={selectOptions}
        ariaLabel={ariaLabel}
        triggerClassName={triggerClassName}
        menuClassName={menuClassName}
        optionClassName={optionClassName}
      />
    </div>
  );
}
